import React, { useRef, useState } from "react";

import {
  ISelection,
  Selection,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { SectionTitle } from "@components/SectionTitle";
import { withPermissions } from "@components/withPermissions";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { Permissions } from "@libs/permissions/permissions.enum";

import { DeployManagementActions } from "./DeployManagementActions";
import {
  DeployManagementFilters,
  DeployManagementFilterState
} from "./DeployManagementFilters";
import { DeployManagementTable } from "./DeployManagementTable";
import { useDeployManagementTenants } from "./useDeployManagementTenants";

const ProsDeployMgmtBase: React.FC = () => {
  const theme = useTheme();

  const [search, setSearch] = useState<string>("");
  const [deploymentRingId, setRingId] = useState<string>("");

  const filters: DeployManagementFilterState = {
    search,
    setSearch,
    deploymentRingId,
    setRingId
  };

  const {
    tenants,
    isLoading,
    error,
    hasNextPage,
    onScrolledToBottom,
    loadingMore
  } = useDeployManagementTenants({
    search,
    deploymentRingId
  });

  // Every shown tenant has a field device in the selected ring, so all rows are selectable.
  const [selectedTenants, setSelectedTenants] = useState<Tenant[]>([]);
  const selection = useRef(
    new Selection<Tenant>({
      getKey: tenant => tenant.id,
      onSelectionChanged: () => {
        setSelectedTenants(selection.current.getSelection());
      }
    })
  );

  return (
    <Stack
      verticalFill
      tokens={{ childrenGap: theme.spacing.s1 }}
      styles={{
        root: {
          height: "100%",
          overflow: "hidden",
          padding: theme.spacing.s1
        }
      }}
    >
      <SectionTitle>Deployment Management</SectionTitle>

      <DeployManagementFilters {...filters} />

      {!deploymentRingId ? (
        <Stack verticalFill verticalAlign="center" horizontalAlign="center">
          <Text variant="large">
            Select a deployment ring to load its tenants.
          </Text>
        </Stack>
      ) : (
        <>
          <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: theme.spacing.m }}
          >
            <Text>
              {selectedTenants.length} of {tenants.length} selected
            </Text>
            {loadingMore && (
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: theme.spacing.s2 }}
              >
                <Spinner size={SpinnerSize.small} />
                <Text variant="small">Loading more tenants…</Text>
              </Stack>
            )}
          </Stack>

          <DeployManagementTable
            items={tenants}
            selection={selection.current as ISelection}
            enableShimmer={isLoading}
            errorMessage={error?.message}
            hasNextPage={hasNextPage}
            onScrolledToBottom={onScrolledToBottom}
          />

          <DeployManagementActions selectedTenants={selectedTenants} />
        </>
      )}
    </Stack>
  );
};

export const ProsDeployMgmt = withPermissions(
  ProsDeployMgmtBase,
  [Permissions.PltCatalogOpsRead, Permissions.PltCatalogOpsWrite],
  "or"
);
